import styled from 'styled-components';
import { Link } from 'react-router-dom';
import useScrollReveal from '../hooks/useScrollReveal';
import WaveDivider from '../components/WaveDivider';
import PageHero from '../components/PageHero';

const PricingSection = styled.section`
  background: var(--cream);
  padding: 100px 24px;
`;

const PricingContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const PricingCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 1px 4px var(--shadow);
  text-align: center;
  transition: transform 0.3s ease;
  border-top: ${({ $featured }) => ($featured ? '4px solid var(--sage)' : 'none')};
  transform: ${({ $featured }) => ($featured ? 'scale(1.04)' : 'none')};

  &:hover {
    transform: ${({ $featured }) => ($featured ? 'scale(1.06)' : 'translateY(-3px)')};
  }
`;

const Badge = styled.span`
  display: inline-block;
  font-family: 'Inter', sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: white;
  background: var(--sage);
  padding: 4px 14px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const CardTitle = styled.h3`
  font-family: 'Lora', serif;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--text-muted);
  margin-bottom: 16px;
`;

const CardPrice = styled.span`
  display: block;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 3rem;
  color: var(--spruce);
  line-height: 1.1;
  margin-bottom: 8px;
`;

const CardDuration = styled.span`
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: var(--text-muted);
`;

const ExplanationSection = styled.section`
  background: var(--spruce-dark);
  padding: 100px 24px;
`;

const ExplanationContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SectionLabel = styled.span`
  display: block;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--sage-light);
  margin-bottom: 16px;
`;

const WhiteHeading = styled.h2`
  font-family: 'Lora', serif;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 600;
  color: white;
  margin-bottom: 32px;
  line-height: 1.3;
`;

const LightBodyText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-light);
  line-height: 1.8;
  margin-bottom: 20px;
`;

const InsuranceSection = styled.section`
  background: var(--frost);
  padding: 100px 24px;
`;

const InsuranceContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SectionHeading = styled.h2`
  font-family: 'Lora', serif;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 32px;
  line-height: 1.3;
`;

const BodyText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-body);
  line-height: 1.8;
  margin-bottom: 20px;
`;

const Callout = styled.div`
  background: var(--mist);
  padding: 24px;
  border-radius: 8px;
  border-left: 3px solid var(--sage);
  margin-top: 32px;
`;

const CalloutText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: var(--text-body);
  line-height: 1.8;
  margin: 0;
`;

const CTASection = styled.section`
  background: var(--mist);
  padding: 100px 24px;
  text-align: center;
`;

const CTAInner = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const CTAHeading = styled.h2`
  font-family: 'Lora', serif;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 16px;
`;

const CTASubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-body);
  line-height: 1.7;
  margin-bottom: 32px;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: var(--sage);
  color: white;
  padding: 14px 36px;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.8rem;
  transition: background 0.3s ease;

  &:hover {
    background: var(--sage-muted);
  }
`;

const SectionImage = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: cover;
  border-radius: 8px;
  margin: 32px 0;
`;

const Fees = () => {
  useScrollReveal();

  return (
    <>
      <PageHero
        label="Fees"
        title="Investment in Your Care"
        subtitle="Equity-based tiered pricing — you choose the rate that fits your capacity."
      />

      <WaveDivider topColor="#e4e8e1" bottomColor="#eef0eb" />

      <PricingSection>
        <PricingContainer>
          <PricingGrid>
            <PricingCard className="fade-in delay-1">
              <CardTitle>Accessibility Rate</CardTitle>
              <CardPrice>$165</CardPrice>
              <CardDuration>/50 minutes</CardDuration>
            </PricingCard>
            <PricingCard $featured className="fade-in delay-2">
              <Badge>Most Common</Badge>
              <CardTitle>Standard Rate</CardTitle>
              <CardPrice>$200</CardPrice>
              <CardDuration>/50 minutes</CardDuration>
            </PricingCard>
            <PricingCard className="fade-in delay-3">
              <CardTitle>Pay-it-Forward Rate</CardTitle>
              <CardPrice>$235</CardPrice>
              <CardDuration>/50 minutes</CardDuration>
            </PricingCard>
          </PricingGrid>
        </PricingContainer>
      </PricingSection>

      <WaveDivider topColor="#eef0eb" bottomColor="#1a2e1a" />

      <ExplanationSection>
        <ExplanationContainer>
          <SectionLabel className="fade-in">How It Works</SectionLabel>
          <WhiteHeading className="fade-in">Equity-Based Pricing</WhiteHeading>
          <LightBodyText className="fade-in delay-1">
            My practice is a private pay, out of network practice. All fees are
            paid at the time of service. I use an equity-based tiered pricing
            model.
          </LightBodyText>
          <LightBodyText className="fade-in delay-2">
            You choose the rate that fits your current capacity. No need to fill
            out any paperwork or justify your choice. Each tier receives the same
            quality of care.
          </LightBodyText>
          <LightBodyText className="fade-in delay-3">
            The accessible rate is not a discount &mdash; it is a community based
            way to increase access to this care while simultaneously
            acknowledging the labor that goes into it. In addition to this model,
            I offer a limited number of spots in my schedule for clients who need significant
            financial assistance.
          </LightBodyText>
          <LightBodyText className="fade-in delay-3">
            Currently all of my significantly reduced spots are taken. Feel free
            to check back here in the future; I will update this page when any
            become available.
          </LightBodyText>
        </ExplanationContainer>
      </ExplanationSection>

      <WaveDivider topColor="#1a2e1a" bottomColor="#e4e8e1" />

      <InsuranceSection>
        <InsuranceContainer className="fade-in">
          <SectionHeading>Insurance Information</SectionHeading>
          <BodyText>
            I do not accept insurance. If your insurance plan has out of network
            benefits, I may be able to provide you with a superbill to submit for
            reimbursement. If you choose to go this route, you are still
            responsible for payment.
          </BodyText>
          <SectionImage
            src="/images/calm-water.jpg"
            alt="Peaceful pond reflecting green forest"
            className="fade-in"
          />
          <Callout>
            <CalloutText>
              If cost is a barrier, please don't hesitate to reach out. I know
              that health insurance costs have changed for many people recently, and I
              am interested in accommodating this as much as possible.
            </CalloutText>
          </Callout>
        </InsuranceContainer>
      </InsuranceSection>

      <WaveDivider topColor="#e4e8e1" bottomColor="#d8ddd5" />

      <CTASection>
        <CTAInner className="fade-in">
          <CTAHeading>Questions About Fees?</CTAHeading>
          <CTASubtitle>
            I'm happy to discuss &mdash; reach out for a conversation.
          </CTASubtitle>
          <CTAButton to="/contact">Contact Me</CTAButton>
        </CTAInner>
      </CTASection>
    </>
  );
};

export default Fees;
